"use client"

import React, { useEffect, useState } from "react"

import { Button } from "@/shared/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { Input } from "@/shared/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog"
import { GetRegionFromLocation } from "@/shared/constants/data/Trip"
import type { Region, TravelPackage } from "@/shared/types/packages"
import { Funnel, Search, X } from "lucide-react"



interface RegionFilterProps {
  selectedRegion: Region | null
  onRegionChange: (region: Region | null) => void
  onFilteredPackagesChange: (packages: TravelPackage[]) => void
  onTravelTypeChange?: (type: TravelType) => void
  allPackages: TravelPackage[]
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
}

const regionLabels: Record<Region, string> = {
  ASIA: "Asia",
  EUROPE: "Europe",
  AFRICA: "Africa",
  AMERICA: "America",
}

export type TravelType = "all" | "international" | "domestic" | "cruise"

export function RegionFilter({ selectedRegion, onRegionChange, onFilteredPackagesChange, onTravelTypeChange, allPackages, searchQuery, onSearchQueryChange }: RegionFilterProps) {
  const regions: Region[] = ["ASIA", "EUROPE", "AFRICA", "AMERICA"]
  const [selectedTravelType, setSelectedTravelType] = React.useState<TravelType>("all")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState(searchQuery)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const regionCounts = React.useMemo(() => {
    const counts: Record<Region, number> = {
      ASIA: 0,
      EUROPE: 0,
      AFRICA: 0,
      AMERICA: 0,
    }

    allPackages.forEach((pkg) => {
      const region = GetRegionFromLocation(pkg.location) as Region
      if (counts[region] !== undefined) {
        counts[region] += 1
      }
    })

    return counts
  }, [allPackages])

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const filterPackages = React.useCallback(() => {
    const filtered = allPackages.filter((pkg) => {
      const matchesRegion = selectedRegion ? GetRegionFromLocation(pkg.location) === selectedRegion : true

      let matchesTravelType = true
      if (selectedTravelType === "international") {
        matchesTravelType = pkg.location !== "Philippines" && !pkg.category.toLowerCase().includes("cruise")
      } else if (selectedTravelType === "domestic") {
        matchesTravelType = pkg.location === "Philippines" && !pkg.category.toLowerCase().includes("cruise")
      } else if (selectedTravelType === "cruise") {
        matchesTravelType = pkg.category.toLowerCase().includes("cruise")
      }

      // Add search functionality
      let matchesSearch = true
      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        const query = debouncedSearchQuery.toLowerCase()
        matchesSearch = (
          pkg.title.toLowerCase().includes(query) ||
          pkg.location.toLowerCase().includes(query) ||
          pkg.category.toLowerCase().includes(query) ||
          pkg.overview.toLowerCase().includes(query) ||
          pkg.highlights.some(highlight => highlight.toLowerCase().includes(query)) ||
          pkg.packageHighlights.some(highlight => highlight.toLowerCase().includes(query))
        )
      }

      return matchesRegion && matchesTravelType && matchesSearch
    })
    onFilteredPackagesChange(filtered)
  }, [selectedRegion, selectedTravelType, debouncedSearchQuery, onFilteredPackagesChange, allPackages])

  useEffect(() => {
    filterPackages()
  }, [filterPackages])

  return (
    <div className="bg-white shadow-lg border-b border-gray-200">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="animate-in fade-in duration-500">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Find Your Perfect Trip</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Tap the filter icon to open search and filter options for destinations.
              </p>
            </div>

            <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-orange-500 hover:text-orange-600 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                >
                  <Funnel className="h-4 w-4" />
                  Filters
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-3xl w-full rounded-3xl p-6 shadow-2xl">
                <DialogHeader className="space-y-2">
                  <DialogTitle className="text-xl font-semibold text-gray-900">Filters</DialogTitle>
                  <DialogDescription className="text-sm text-gray-500">
                    Search and narrow your results by destination, region, or travel type.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {onSearchQueryChange && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search packages by destination, category, or highlights..."
                        value={searchQuery || ''}
                        onChange={(e) => onSearchQueryChange(e.target.value)}
                        className="pl-10 pr-10 py-3 text-base border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => onSearchQueryChange('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          aria-label="Clear search"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Region</h4>
                        <p className="text-sm text-gray-500">Choose one or more regions to focus your search.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {regions.map((region) => (
                        <Button
                          key={region}
                          variant={selectedRegion === region ? 'default' : 'outline'}
                          onClick={() => onRegionChange(selectedRegion === region ? null : region)}
                          className="rounded-full px-4 py-2 text-sm"
                        >
                          {regionLabels[region]} ({regionCounts[region]})
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-[1fr_auto] items-end">
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-900">Travel Type</h4>
                      <p className="text-sm text-gray-500">Filter by domestic, international, or cruise packages.</p>
                      <Select
                        value={selectedTravelType}
                        onValueChange={(value) => {
                          const v = value as TravelType
                          setSelectedTravelType(v)
                          onTravelTypeChange?.(v)
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-[240px] h-12">
                          <SelectValue placeholder="Travel Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="international">International</SelectItem>
                          <SelectItem value="domestic">Domestic</SelectItem>
                          <SelectItem value="cruise">Cruise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => {
                        onRegionChange(null)
                        setSelectedTravelType('all')
                        onTravelTypeChange?.('all')
                        onSearchQueryChange?.('')
                      }}
                      className="h-12 rounded-full px-5 text-sm"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                <DialogFooter className="justify-between gap-3 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500">Filters update instantly while you type.</div>
                  <Button
                    onClick={() => setIsFilterOpen(false)}
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 py-3"
                  >
                    Apply Filters
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex flex-wrap gap-2">
            {selectedRegion && (
              <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-700">
                {regionLabels[selectedRegion]}
              </span>
            )}
            {selectedTravelType !== 'all' && (
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700 capitalize">
                {selectedTravelType}
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                Search: {searchQuery}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
